<template>
  <article class="content row align-center">
    <div class="large-11 columns">
      <header>
        <div class="content__title content__title--inline">
          <h1>{{ $t('PUBLISHER_ACCOUNT.TITLE') }}</h1>
          &nbsp;
          <ElPopover
            ref="accountStatePopover"
            :placement="popoverPlacement"
            width="250"
            trigger="hover"
            :content="getAccountState('message')"
          />

          <span
            v-show="user.state"
            v-popover:accountStatePopover
            class="chip chip--aligned-middle"
            :class="{
              'chip--success': isVerified,
              'chip--warning': isDefective,
              'chip--alert': isBlocked,
              'chip--info':
                isModified ||
                isNotReviewed ||
                isAcceptedForFreeProduct ||
                isAcceptedWithModifiedFinance,
            }"
          >
            {{ getAccountState('label') }}
          </span>
        </div>

        <p class="content__description">
          {{ $t('PUBLISHER_ACCOUNT.DESC') }}
        </p>
      </header>

      <hr class="divider" />

      <section class="form-section">
        <h1 class="form-section__title">
          {{ $t('PUBLISHER_ACCOUNT.ACCOUNT_INFO') }}
        </h1>

        <div class="row align-justify">
          <div class="small-12 large-4 columns">
            <p class="form-section__description">
              <RouterLinkFormat
                :links="{
                  support: { to: { path: '/support' }, text: $t('SUPPORT') },
                }"
              >
                {{ $t('EDIT_DISABLED_DESC') }}
                ({{ $t('PUBLISHER_EMAIL_CAN_NOT_CHANGE') }})
              </RouterLinkFormat>
            </p>
          </div>

          <div class="small-12 large-7 columns">
            <div class="card">
              <div class="card__block">
                <dl class="list list--definition list--clean">
                  <dt>{{ $t('EMAIL') }}</dt>
                  <dd class="text-latin publisher-email">
                    {{ user.email }}
                  </dd>

                  <dt>{{ $t('COUNTRY') }}</dt>
                  <dd>{{ getCountryName(user.country) }}</dd>

                  <dt>{{ $t('PUBLISHER_ACCOUNT.ACCOUNT_TYPE') }}</dt>
                  <dd>{{ accountType }}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr class="divider" />

      <DomesticNatural
        v-if="isDomesticNatural"
        :legal-info="legalInfo"
        :is-verified="isVerified"
        @updatedLegalInfo="updateLegalInfo"
      />

      <ForeignerNatural
        v-else-if="isForeignerNatural"
        :legal-info="legalInfo"
        :is-verified="isVerified"
        @updatedLegalInfo="updateLegalInfo"
      />

      <DomesticLegal
        v-else-if="isDomesticLegal"
        :legal-info="legalInfo"
        :is-verified="isVerified"
        @updatedLegalInfo="updateLegalInfo"
      />

      <ForeignerLegal
        v-else-if="isForeignerLegal"
        :legal-info="legalInfo"
        :is-verified="isVerified"
        @updatedLegalInfo="updateLegalInfo"
      />

      <hr class="divider" />

      <ContactInfo
        v-if="user.phone"
        :user-data="user"
        @data-updated="updateLocalUserData"
      />
    </div>
  </article>
</template>

<script>
import DomesticLegal from './DomesticLegal';
import DomesticNatural from './DomesticNatural';
import ForeignerLegal from './ForeignerLegal';
import ForeignerNatural from './ForeignerNatural';
import ContactInfo from './ContactInfo';
import RouterLinkFormat from '@@/common/RouterLinkFormat';

import { Publishers } from '@/api/api';

import { DIRECTION } from '@/store/types';

import countries from '@/utils/countries';
import layout from '@/mixins/layout';

export default {
  name: 'Account',

  components: {
    RouterLinkFormat,
    DomesticLegal,
    DomesticNatural,
    ForeignerLegal,
    ForeignerNatural,
    ContactInfo,
  },

  mixins: [layout],

  data() {
    return {
      user: {},
      legalInfo: {},
    };
  },

  computed: {
    isDefective() {
      return this.user.state === 'D';
    },

    isVerified() {
      return this.user.state === 'A';
    },

    isBlocked() {
      return this.user.state === 'B';
    },

    isModified() {
      return ['F', 'M'].includes(this.user.state);
    },

    isNotReviewed() {
      return this.user.state === 'N';
    },

    isAcceptedForFreeProduct() {
      return this.user.state === 'J';
    },

    isAcceptedWithModifiedFinance() {
      return this.user.state === 'R';
    },

    accountType() {
      switch (this.user.account_type) {
        case 'N':
          return this.$t('NATURAL');
        case 'L':
          return this.$t('LEGAL');
        default:
          return '';
      }
    },

    isNatural() {
      return this.user.account_type === 'N';
    },

    isDomestic() {
      return this.user.country === 'IR';
    },

    isDomesticNatural() {
      return this.isNatural && this.isDomestic;
    },

    isForeignerNatural() {
      return this.isNatural && !this.isDomestic;
    },

    isDomesticLegal() {
      return !this.isNatural && this.isDomestic;
    },

    isForeignerLegal() {
      return !this.isNatural && !this.isDomestic;
    },

    dir() {
      return this.$store.getters[DIRECTION];
    },

    popoverPlacement() {
      if (this.dir === 'rtl') {
        return 'left';
      }
      return 'right';
    },
  },

  created() {
    // set the page title
    this.setPageTitle('SETTINGS');

    // fetch the publisher data
    this.getPublisher();
  },

  methods: {
    getPublisher() {
      return Publishers.get()
        .then(({ body: { publisher } }) => {
          const user = {
            account_type: publisher.account_type,
            country: publisher.country,
            email: publisher.email,
            state: publisher.state,
            ...publisher.contact_info,
          };
          this.legalInfo = publisher.identification_info;
          this.user = user;
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
        });
    },

    getCountryName(code, list = countries) {
      return list
        .filter(country => country.code === code)
        .map(country => country.name)[0];
    },

    getAccountState(type) {
      /* eslint-disable no-param-reassign */
      type = type.toUpperCase();
      const { state } = this.user;
      const stateMap = {
        A: `PUBLISHER_ACCOUNT.VERIFIED_${type}`,
        N: `PUBLISHER_ACCOUNT.NOT_REVIEWED_${type}`,
        B: `PUBLISHER_ACCOUNT.BLOCKED_${type}`,
        D: `PUBLISHER_ACCOUNT.DEFECTIVE_${type}`,
        F: `PUBLISHER_ACCOUNT.MODIFIED_${type}`,
        J: `PUBLISHER_ACCOUNT.ACCEPTED_FOR_FREE_PRODUCTS_${type}`,
        R: `PUBLISHER_ACCOUNT.ACCEPTED_WITH_MODIFIED_FINANCE_${type}`,
      };

      if (state in stateMap) {
        return this.$t(stateMap[state]);
      }

      return '';
    },

    updateLegalInfo(legalInfo) {
      this.legalInfo = Object.assign({}, legalInfo);

      this.$set(this.user, 'state', 'F');
    },

    updateLocalUserData(updatedUser) {
      this.user = {
        ...updatedUser,
        state: 'F',
      };
    },
  },
};
</script>

<style lang="scss">
.publisher-email {
  direction: ltr;
  text-align: left;

  @include rtl() {
    text-align: right;
  }
}
</style>
